/* Dedicated NIR signup step (SNDS appariement) — shown right after the
 * OTP-verified inscription step for roles with nir_collection_mode ≠ HIDDEN
 * (see helper/nir.ts shouldCollectNir, shared with the progress-bar spine).
 *
 * SINGLE FIELD BY DESIGN: the NIR alone fully identifies its holder at the
 * CNAM — no birthdate is asked here, for any study. When a study links a
 * BENEFICIARY's data (e.g. Outborn: the child matched under the parent's
 * NIR "ouvrant droit"), the beneficiary's sex/birthdate come from study
 * data, never from this page.
 *
 * LAYOUT: everything is normal document flow — title → intro card → input →
 * NextPrev pinned to the bottom by Container's flex column. Deliberately NOT
 * the absolutely-positioned <ScreenTitle> (top: 40%), which floats over the
 * content; and no setResonanceLayout title either — the signup flow runs the
 * layout in welcomePage mode, where the header renders no title at all.
 *
 * PERSISTENCE: like every other identity field (name/email/phone), the NIR is
 * NOT sent to the server here. It is validated (mod-97) and handed up to
 * HomeContent, which holds it in a dedicated variable (pendingNir) and posts
 * it to the dedicated cloisonné endpoint ONLY at consent time, right before
 * the consent itself — someone who abandons signup leaves no NIR behind.
 * It must never be merged into the consent payload (postConsentRequestType):
 * that path writes to the users row, which the cloisonnement forbids.
 */
import { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import styled from "styled-components";
import Container from "../../../atoms/Container";
import { FieldError } from "../../../atoms/LoginTextField";
import NextPrevButtons from "../../../molecules/NextPrevButtons";
import Question from "../../../molecules/Question/Question";
import useVocalisation from "../../../../hooks/useVocalisation";
import { useConfig } from "../../../../contexts/ConfigContext";
import { useHomeContext } from "../../../../pages/Home/HomeContent";
import { getNirCollectionMode, isValidNir, normalizeNir } from "../../../../helper/nir";
import {
  QuestionTextTypeEnum,
  QuestionTypeEnum,
  TextQuestionType,
} from "../../../../types/Question";

// Rendered through the real questionnaire Question component so the field
// shares the exact same chrome (title, surface, IonItem input) as every other
// question in the app.
const buildNirQuestion = (title: string, description: string, placeholder: string, isMandatory: boolean): TextQuestionType => ({
  id: -1,
  title,
  // The format explanation lives in the card's description slot; the input's
  // placeholder shows the bare example (the generic "type your text here"
  // copy would be misleading for a number).
  description,
  export_code: "nir",
  integral: { is_integral: false, integral_errorcode: null },
  // Optional mode: no mandatory star — leaving it empty and pressing Next IS
  // the skip (there is no separate skip button).
  is_mandatory_question: isMandatory,
  is_active: true,
  score_coefficient: 0,
  order: 9998,
  version: "1",
  type: QuestionTypeEnum.TEXT,
  // LINE = single-line free text (Corsican NIRs contain 2A/2B, so never a
  // number input); normalizeNir strips spaces/dots/dashes on submit.
  detail: { type: QuestionTextTypeEnum.LINE, placeholder },
});

interface DisplayNirProps {
  // The value previously entered this session (back-navigation prefill).
  initialNir: string | null;
  // Called with the normalized NIR — or null when skipped (OPTIONAL mode).
  // HomeContent stores it and resumes the stratification → sites → consent
  // chain; the actual POST happens at consent time.
  onCompleted: (nir: string | null) => void;
}

const DisplayNir = ({ initialNir, onCompleted }: DisplayNirProps): ReactElement => {
  const { t } = useTranslation();
  const { readText } = useVocalisation();
  const { selectedRole } = useConfig();
  const { goPrevious } = useHomeContext();
  const isOptional = getNirCollectionMode(selectedRole) === "OPTIONAL";

  const { values, touched, errors, submitForm, setFieldValue } =
    useFormik({
      initialValues: { nir: initialNir ?? "" },
      validationSchema: Yup.object().shape({
        // OPTIONAL mode has no separate skip button: an EMPTY field + Next IS
        // the skip. Anything typed must still pass the checksum.
        nir: isOptional
          ? Yup.string().test("nir-checksum-if-filled", "form_error_nir", (value) => !value || isValidNir(value))
          : Yup.string()
            .required("form_error_required")
            .test("nir-checksum", "form_error_nir", (value) => isValidNir(value)),
      }),
      onSubmit: ({ nir }) => {
        onCompleted(nir ? normalizeNir(nir) : null);
      },
    });

  return (
    <Container>
      <Content>
        <Title>
          {isOptional
            ? `${t("home_nir_title")} (${t("home_nir_optional_label")})`
            : t("home_nir_title")}
        </Title>
        <IntroCard>
          <IntroText>{t("home_nir_explanation_snds")}</IntroText>
        </IntroCard>

        <Question
          question={buildNirQuestion(
            t("home_nir_field_label"),
            t("home_nir_placeholder"),
            t("home_nir_input_placeholder"),
            !isOptional
          )}
          answer={values.nir ?? ""}
          onAnswerChange={(value) => setFieldValue("nir", (value as string) ?? "")}
          onEnterNext={submitForm}
          readText={readText}
          cardMargin="12px 20px"
        />
        {/* Rendered as a REAL error (red, danger styling) — the shared
            QuestionDisplay ruleMessage slot shows grey description-style text
            that reads as a hint, not a blocker. */}
        {touched.nir && errors.nir && (
          <NirFieldError>{t(errors.nir as string)}</NirFieldError>
        )}
      </Content>

      <NextPrevButtons
        onPreviousClick={goPrevious}
        onNextClick={submitForm}
      />
    </Container>
  );
};

// Normal-flow content block; NextPrevButtons (margin-top: auto) stays pinned
// to the bottom of the flex Container.
const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

// In-flow title (the welcomePage layout renders no header title, and the
// absolute ScreenTitle atom overlaps content — never use it here).
const Title = styled.h1`
  margin: 18px 50px 4px;
  font-size: 20px;
  font-weight: bold;
  color: var(--app-color-text, #393939);
`;

// Flat, app-like intro surface (same margin rhythm as FormGroup: 15px 50px).
const IntroCard = styled.div`
  margin: 15px 50px 5px;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(var(--ion-color-primary-rgb, 45, 182, 180), 0.08);
`;

const IntroText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--app-color-text, #393939);
`;

// Same danger styling as the field errors, aligned with the question card.
const NirFieldError = styled(FieldError)`
  margin: 6px 25px 0;
`;

export default DisplayNir;
